import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { taskAdminApis } from '@gleamer/apis';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAdminTasks } from '../../../../hooks/useAdminTasks';
import { usePermissions } from '../../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../../utils/permissions';
import { BUTTON_TONES, PageButton } from '../../../atoms/PageButton';
import { PageTitle } from '../../../atoms/PageTitle';
import {
  Card,
  Field,
  inputClassName,
  Pager,
  selectClassName,
} from '../../../atoms/Surface';
import { TaskDrawer } from './TaskDrawer';
import { TasksTable } from './TasksTable';
import {
  DEFAULT_PAGE_SIZE,
  Feature,
  FEATURES,
  filterTasks,
  groupTasks,
  Grouping,
  GROUPINGS,
  listHasTaskers,
  namePrefix,
  PAGE_SIZES,
  paginate,
  reviewersOf,
  Sort,
  SORTS,
  summarise,
  TASK_STATUSES,
  TaskStatus,
  toCsv,
} from './tasks';

/** The parameters a change of view starts again from page 1 for. */
const VIEW_PARAMS = [
  'q',
  'status',
  'feature',
  'creator',
  'prefix',
  'person',
  'sort',
  'group',
  'size',
];

/** The page's state, kept in the URL so a filtered view can be shared or bookmarked. */
function useTaskParams() {
  const [params, setParams] = useSearchParams();
  const pick = <T extends string>(
    name: string,
    allowed: readonly string[],
    fallback: T
  ): T =>
    (allowed.includes(params.get(name) ?? '')
      ? params.get(name)
      : fallback) as T;
  const size = Number(params.get('size'));
  const state = {
    search: params.get('q') ?? '',
    status: pick<TaskStatus | 'all'>(
      'status',
      TASK_STATUSES.map(s => s.value),
      'all'
    ),
    feature: pick<Feature | 'any'>(
      'feature',
      FEATURES.map(f => f.value),
      'any'
    ),
    creator: params.get('creator') ?? '',
    prefix: params.get('prefix') ?? '',
    person: params.get('person') ?? '',
    sort: pick<Sort>('sort', Object.keys(SORTS), 'newest'),
    group: pick<Grouping>('group', Object.keys(GROUPINGS), 'none'),
    /** Zero-based here, one-based in the URL. */
    page: Math.max(0, (parseInt(params.get('page') ?? '1', 10) || 1) - 1),
    size: PAGE_SIZES.includes(size) ? size : DEFAULT_PAGE_SIZE,
    task: params.has('task') ? Number(params.get('task')) : null,
  };
  const set = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([name, value]) =>
      value ? next.set(name, value) : next.delete(name)
    );
    if (Object.keys(changes).some(name => VIEW_PARAMS.includes(name))) {
      next.delete('page');
    }
    setParams(next, { replace: true });
  };
  return { ...state, set };
}

/** A figure over the table: a neutral tile, a dot of its status's colour; picking it filters the table. */
function SummaryTile({
  label,
  value,
  active,
  dot = 'bg-dl-faint',
  onClick,
}: {
  label: string;
  value: React.ReactNode;
  active?: boolean;
  dot?: string;
  onClick?: () => void;
}) {
  const content = (
    <>
      <span className="flex items-center gap-2 text-base font-medium text-dl-muted">
        <span aria-hidden className={classNames('h-2 w-2 rounded-full', dot)} />
        {label}
      </span>
      <span className="text-3xl font-semibold tabular-nums tracking-tight text-dl-text">
        {value}
      </span>
    </>
  );
  const className = classNames(
    'flex flex-col gap-1 rounded-xl border px-4 py-3.5 text-left transition',
    active ? 'border-dl-accent bg-dl-tint' : 'border-dl-border bg-dl-surface'
  );
  return onClick ? (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={classNames(className, { 'hover:border-dl-strong': !active })}
    >
      {content}
    </button>
  ) : (
    <div className={className}>{content}</div>
  );
}

function download(name: string, text: string) {
  const url = URL.createObjectURL(
    new Blob([text], { type: 'text/csv;charset=utf-8' })
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

function Select({
  label,
  value,
  onChange,
  children,
  className,
}: React.PropsWithChildren<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
}>) {
  return (
    <Field label={label}>
      <select
        className={classNames(selectClassName, className)}
        value={value}
        onChange={e => onChange(e.target.value)}
      >
        {children}
      </select>
    </Field>
  );
}

/**
 * The data managers' tasks — what they kept in Notion's "Tasks G3D" table, from what the platform holds: every
 * task read at once (GET /api/label-tasks), searched, filtered, sorted, grouped and paged here, its details
 * read when it is opened. Items and progress are not in the list; neither are the dataset queries, which have
 * no home on the platform yet.
 */
export function TasksPage() {
  const query = useTaskParams();
  const { has } = usePermissions();
  const tasksQuery = useAdminTasks();
  const [folded, setFolded] = useState<Set<string>>(new Set());

  const all = useMemo(() => tasksQuery.data?.content ?? [], [tasksQuery.data]);
  const total = tasksQuery.data?.totalElements ?? 0;
  // 2,000 tasks filter and sort in a few milliseconds: no memo to keep in step with the URL
  const shown = filterTasks(all, query);
  const grouped = groupTasks(shown, query.group);
  const paged = paginate(
    grouped,
    query.group === 'none' ? new Set() : folded,
    query.page,
    query.size
  );
  const summary = useMemo(() => summarise(all), [all]);
  const creators = useMemo(
    () => [...new Set(all.map(t => t.createdBy))].sort(),
    [all]
  );
  const prefixes = useMemo(
    () => [...new Set(all.map(namePrefix))].sort(),
    [all]
  );
  const people = useMemo(
    () =>
      [
        ...new Set(all.flatMap(t => [...(t.taskers ?? []), ...reviewersOf(t)])),
      ].sort(),
    [all]
  );
  const withTaskers = listHasTaskers(all);
  const filtered = !!(
    query.search ||
    query.status !== 'all' ||
    query.feature !== 'any' ||
    query.creator ||
    query.prefix ||
    query.person
  );

  const clearFilters = () =>
    query.set({
      q: null,
      status: null,
      feature: null,
      creator: null,
      prefix: null,
      person: null,
    });

  const toggle = (key: string) =>
    setFolded(current => {
      const next = new Set(current);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const open = (taskId: number) => query.set({ task: String(taskId) });

  return (
    <div className="flex flex-col gap-5">
      <PageTitle title="Tasks" className="flex-wrap items-center gap-3">
        {tasksQuery.isSuccess && (
          <span className="flex-auto text-xl tabular-nums text-dl-faint">
            {filtered
              ? `${shown.length.toLocaleString()} of ${all.length.toLocaleString()} tasks`
              : `${all.length.toLocaleString()} tasks`}
          </span>
        )}
        <span className="flex items-center gap-2">
          <PageButton
            onClick={() => download('tasks.csv', toCsv(shown))}
            disabled={shown.length === 0}
            className="mx-0 h-10 px-4"
          >
            Export CSV
          </PageButton>
          <PageButton
            onClick={() => tasksQuery.refetch()}
            disabled={tasksQuery.isFetching}
            className="mx-0 h-10 px-4"
          >
            {tasksQuery.isFetching ? 'Refreshing…' : 'Refresh'}
          </PageButton>
          {has(PERMISSIONS.CREATE_LABEL_TASK) && (
            <Link
              to="/backoffice/tasks/new"
              className={classNames(
                'inline-flex h-10 items-center gap-2 rounded-lg border px-4 transition',
                BUTTON_TONES.primary
              )}
            >
              <PlusIcon aria-hidden className="h-4 w-4" />
              New task
            </Link>
          )}
        </span>
      </PageTitle>

      {tasksQuery.isSuccess && (
        <div
          role="group"
          aria-label="Quick filters"
          className="grid grid-cols-[repeat(auto-fit,minmax(9.5rem,1fr))] gap-3"
        >
          <SummaryTile
            label="All tasks"
            value={summary.total.toLocaleString()}
            active={!filtered}
            onClick={clearFilters}
          />
          <SummaryTile
            label="Active"
            value={summary.active.toLocaleString()}
            dot="bg-dl-success"
            active={query.status === 'active'}
            onClick={() => query.set({ status: 'active' })}
          />
          <SummaryTile
            label="Items failed"
            value={summary.itemsFailed.toLocaleString()}
            dot="bg-dl-danger"
            active={query.status === 'compute-error'}
            onClick={() => query.set({ status: 'compute-error' })}
          />
          <SummaryTile
            label="Predictions failed"
            value={summary.predictionsFailed.toLocaleString()}
            dot="bg-dl-danger"
            active={query.status === 'import-error'}
            onClick={() => query.set({ status: 'import-error' })}
          />
          <SummaryTile
            label="Disabled"
            value={summary.disabled.toLocaleString()}
            active={query.status === 'disabled'}
            onClick={() => query.set({ status: 'disabled' })}
          />
          <SummaryTile
            label="Created in 30 days"
            dot="bg-dl-info"
            value={summary.lastThirtyDays.toLocaleString()}
          />
        </div>
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <label className="relative min-w-[16rem] flex-1">
            <span className="sr-only">Search tasks</span>
            <MagnifyingGlassIcon
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-dl-faint"
            />
            <input
              type="search"
              value={query.search}
              placeholder="Search name, id, description, table, people"
              onChange={e => query.set({ q: e.target.value || null })}
              className={classNames(inputClassName, 'w-full pl-10')}
            />
          </label>
          <Select
            label="Sort"
            value={query.sort}
            onChange={value =>
              query.set({ sort: value === 'newest' ? null : value })
            }
          >
            {Object.entries(SORTS).map(([value, { label }]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <Select
            label="Group by"
            value={query.group}
            onChange={value =>
              query.set({ group: value === 'none' ? null : value })
            }
          >
            {Object.entries(GROUPINGS).map(([value, { label }]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <Select
            label="Status"
            value={query.status}
            onChange={value =>
              query.set({ status: value === 'all' ? null : value })
            }
          >
            <option value="all">All</option>
            {TASK_STATUSES.map(s => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
          <Select
            label="Setup"
            value={query.feature}
            onChange={value =>
              query.set({ feature: value === 'any' ? null : value })
            }
          >
            <option value="any">Any</option>
            {FEATURES.map(f => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </Select>
          <Select
            label="Name prefix"
            value={query.prefix}
            onChange={value => query.set({ prefix: value || null })}
          >
            <option value="">All</option>
            {prefixes.map(p => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Select>
          {withTaskers && (
            <Select
              label="Labeller or reviewer"
              value={query.person}
              onChange={value => query.set({ person: value || null })}
              className="max-w-[16rem]"
            >
              <option value="">Anyone</option>
              {people.map(p => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          )}
          <Select
            label="Created by"
            value={query.creator}
            onChange={value => query.set({ creator: value || null })}
            className="max-w-[14rem]"
          >
            <option value="">Anyone</option>
            {creators.map(c => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          {filtered && (
            <PageButton onClick={clearFilters} className="mx-0 h-10 px-5">
              Clear filters
            </PageButton>
          )}
        </div>
      </Card>

      {total > all.length && (
        <p className="text-base text-dl-muted">
          Showing the {taskAdminApis.ADMIN_TASKS_LIMIT.toLocaleString()} newest
          tasks out of {total.toLocaleString()}: search and filters see only
          those.
        </p>
      )}
      {tasksQuery.isSuccess &&
        !withTaskers &&
        (query.group === 'labeller' || query.group === 'reviewer') && (
          <p className="text-base text-dl-warning">
            This viewer-api's task list does not carry the labellers yet: every
            task shows under “No labeller”.
          </p>
        )}
      {tasksQuery.isLoading && <Card>Loading the tasks…</Card>}
      {tasksQuery.isError && (
        <Card>
          <p role="alert" className="text-dl-danger">
            Could not load the tasks: {(tasksQuery.error as Error)?.message}
          </p>
        </Card>
      )}
      {tasksQuery.isSuccess && shown.length === 0 && (
        <Card>
          <p className="py-8 text-center text-lg text-dl-muted">
            {all.length === 0
              ? 'No task yet.'
              : 'No task matches: try another search or filter.'}
          </p>
        </Card>
      )}

      {paged.groups.map(group =>
        query.group === 'none' ? (
          // the table runs to the card's top edge: its header row is the card's first line
          <Card key="all" className="overflow-hidden pt-0">
            <TasksTable
              tasks={group.rows}
              openTaskId={query.task}
              onOpen={open}
            />
          </Card>
        ) : (
          <Card key={group.key}>
            <button
              type="button"
              aria-expanded={!folded.has(group.key)}
              onClick={() => toggle(group.key)}
              className="flex w-full items-center gap-2 text-left text-xl font-semibold text-dl-text"
            >
              <span
                aria-hidden
                className={classNames('text-dl-faint transition', {
                  '-rotate-90': folded.has(group.key),
                })}
              >
                ▾
              </span>
              {group.key}
              <span className="font-normal tabular-nums text-dl-faint">
                ({group.tasks.length})
              </span>
              {group.continued && (
                <span className="text-base font-normal text-dl-faint">
                  · continued
                </span>
              )}
            </button>
            {group.rows.length > 0 && (
              <div className="mt-3">
                <TasksTable
                  tasks={group.rows}
                  openTaskId={query.task}
                  onOpen={open}
                />
              </div>
            )}
          </Card>
        )
      )}

      {tasksQuery.isSuccess && paged.rows > 0 && (
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[20rem] flex-1">
            <Pager
              page={paged.page}
              pages={paged.pages}
              total={paged.rows}
              noun={query.group === 'none' ? 'tasks' : 'rows'}
              onPage={next =>
                query.set({ page: next > 0 ? String(next + 1) : null })
              }
            />
          </div>
          <Select
            label="Per page"
            value={String(query.size)}
            onChange={value =>
              query.set({
                size: Number(value) === DEFAULT_PAGE_SIZE ? null : value,
              })
            }
          >
            {PAGE_SIZES.map(size => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Select>
        </div>
      )}

      {query.task !== null && (
        <TaskDrawer
          taskId={query.task}
          listed={all.find(task => task.taskId === query.task)}
          onClose={() => query.set({ task: null })}
          canDuplicate={has(PERMISSIONS.CREATE_LABEL_TASK)}
          canSettlePredictions={has(PERMISSIONS.CREATE_PREDICTION)}
          canStopComputations={has(PERMISSIONS.UPDATE_LABEL_TASK)}
        />
      )}
    </div>
  );
}

export default TasksPage;
